import React, { useCallback, useEffect, useState } from 'react';
import { PenTool, RefreshCw, Clock, AlertTriangle, Trash2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { firmasAPI } from '../api';
import CancelarFirmaDialog from './CancelarFirmaDialog';
import CompartirEnlaceFirma from './CompartirEnlaceFirma';
import { toast } from '../hooks/use-toast';

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '');

// Aviso del expediente: documentos enviados al cliente que siguen sin firmar.
// No se muestra nada si el expediente no tiene firmas pendientes ni caducadas.
const FirmasExpedientePanel = ({ expedienteId }) => {
  const [firmas, setFirmas] = useState([]);
  const [renovando, setRenovando] = useState(null);
  const [gestionar, setGestionar] = useState(null);

  const cargar = useCallback(async () => {
    if (!expedienteId) return;
    try {
      const res = await firmasAPI.getByExpediente(expedienteId);
      const datos = Array.isArray(res.data) ? res.data : [];
      setFirmas(datos.filter((f) => f.estado === 'pendiente' || f.estado === 'caducada'));
    } catch (e) {
      setFirmas([]);
    }
  }, [expedienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const refrescar = () => cargar();
    window.addEventListener('lexduo:firmas', refrescar);
    return () => window.removeEventListener('lexduo:firmas', refrescar);
  }, [cargar]);

  const renovar = async (id) => {
    setRenovando(id);
    try {
      const res = await firmasAPI.renovar(id, { enviar_email: true });
      toast({
        title: res.data?.email_enviado ? 'Enlace reenviado al cliente' : 'Enlace nuevo creado',
        description: res.data?.email_enviado
          ? `Enviado a ${res.data?.email_enviado_a || 'el cliente'}`
          : 'No se pudo enviar el email: copia el enlace desde el diálogo de firma',
        variant: res.data?.email_enviado === false ? 'destructive' : 'default',
      });
      cargar();
    } catch (e) {
      toast({ title: 'No se pudo renovar', description: e?.response?.data?.detail || 'Error inesperado',
              variant: 'destructive' });
    } finally {
      setRenovando(null);
    }
  };

  if (firmas.length === 0) return null;

  const caducadas = firmas.filter((f) => f.estado === 'caducada').length;

  return (
    <div className="bg-white border border-amber-200 rounded-lg" data-testid="expediente-firmas-panel">
      <div className="flex items-center gap-2 px-6 py-3 border-b border-amber-100">
        <PenTool className="w-4 h-4 text-indigo-600" />
        <span className="font-semibold text-gray-900">Firmas pendientes de este expediente</span>
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"
               data-testid="expediente-firmas-total">{firmas.length}</Badge>
        {caducadas > 0 && (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100"
                 data-testid="expediente-firmas-caducadas">
            {caducadas} caducada{caducadas === 1 ? '' : 's'}
          </Badge>
        )}
      </div>
      <div className="divide-y">
        {firmas.map((f) => (
          <div key={f.id} className="flex items-center gap-3 px-6 py-3"
               data-testid={`expediente-firma-${f.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 truncate">{f.nombre_documento}</div>
              <div className="text-xs text-gray-500 truncate">
                {f.firmante_nombre || 'cliente'}
                {f.email_enviado_a ? ` · ${f.email_enviado_a}` : ' · enlace manual'}
                {f.estado === 'caducada'
                  ? ` · caducó el ${fecha(f.expira_en)}`
                  : ` · caduca el ${fecha(f.expira_en)}`}
                {f.recordatorios ? ` · ${f.recordatorios} recordatorio(s) enviado(s)` : ''}
              </div>
            </div>
            {f.estado === 'caducada' ? (
              <Badge className="bg-red-100 text-red-700 hover:bg-red-100 flex-shrink-0">
                <AlertTriangle className="w-3 h-3 mr-1" /> Caducada
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 flex-shrink-0">
                <Clock className="w-3 h-3 mr-1" />
                {f.dias_esperando === 0 ? 'enviado hoy' : `${f.dias_esperando} días esperando`}
              </Badge>
            )}
            <CompartirEnlaceFirma firma={f} />
            <Button size="sm" variant="outline" className="h-8 flex-shrink-0"
                    disabled={renovando === f.id} onClick={() => renovar(f.id)}
                    title="Generar un enlace nuevo y reenviarlo al cliente"
                    data-testid={`expediente-firma-renovar-${f.id}`}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1 text-indigo-600 ${renovando === f.id ? 'animate-spin' : ''}`} />
              Renovar enlace
            </Button>
            <Button size="sm" variant="ghost" className="h-8 flex-shrink-0"
                    onClick={() => setGestionar(f)}
                    title="Cancelar o eliminar la petición"
                    data-testid={`expediente-firma-cancelar-${f.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-600" />
            </Button>
          </div>
        ))}
      </div>
      <CancelarFirmaDialog
        firma={gestionar}
        open={!!gestionar}
        onOpenChange={(v) => { if (!v) setGestionar(null); }}
        onDone={() => { setGestionar(null); cargar(); }}
      />
      <div className="px-6 py-2 text-xs text-amber-700 border-t border-amber-100">
        Con «Copiar enlace» vuelves a enviar el mismo enlace por WhatsApp, email o SMS sin crear
        una firma nueva. Se reenvía además automáticamente al cliente a los 3 y 7 días. Si caduca
        sin firmarse, el despacho recibe un aviso por email.
      </div>
    </div>
  );
};

export default FirmasExpedientePanel;
